import {
  List as IList,
  Set as ISet,
} from 'immutable';
import { Duration } from 'luxon';
import {
  Component,
  For,
  JSXElement,
  Resource,
  Show,
  createMemo,
  createResource,
  useContext,
} from 'solid-js';
import {
  ActionDropdown,
  AppPage,
  AuthContext,
  Button,
  DeployProviderIcon,
  Dropdown,
  EntryContextProvider,
  FaIcon,
  FormatDate,
  Link,
  LocalizationContext,
  MarkdownView,
  MarkdownViewPlainTextLine,
  PackageActionButton,
  PageNotFound,
  PrettyDataSize,
  ProjectActionsButtons,
  ProjectUserRole,
  ResourceFallback,
  Routes,
  Tags,
  UploadBox,
  WebPlayer,
  allTags,
  callDialog,
  compareProjectUserRoles,
  projectUserRoles,
  readEntriesFromArchive,
  useRoutingNavigate,
} from '../../components';
import {
  BranchInfo,
  DeploySiteInfo,
  PackageInfo,
  UserInfo,
  VersionInfo,
} from '../../components/api-generated';
import config from '../../config';
import { AddDeploySiteDialog } from '../../dialogs/add_deploy_site';
import {
  ConfirmDialog,
  OkCopyDialog,
  PromptDialog,
  showErrorScope,
} from '../../dialogs/basic';
import { DeployToSiteDialog } from '../../dialogs/deploy_to_site';
import { EditPackageTagsDialog } from '../../dialogs/edit_package_tags';
import { PromptMarkdownDialog } from '../../dialogs/markdown';
import { NewVersionDialog } from '../../dialogs/new_version';
import { PublishExistingVersionDialog } from '../../dialogs/publish_existing_version';
import { PublishVersionDialog } from '../../dialogs/publish_version';
import {
  linkInvite,
  linkProject,
  linkProjectBranch,
  linkProjectBranchVersion,
  linkProjectBranchVersionPackage,
  linkProjectManage,
  linkProjects,
  linkStoreProject,
} from './links';

export const RouteProjects: Component = () => {
  const { api, session } = useContext(AuthContext)!;
  const { t } = useContext(LocalizationContext)!;
  const navigate = useRoutingNavigate();

  const [projects, { refetch: refetchProjects }] = createResource(async () => {
    return await api.getUsersMeProjects()
  });
  const projectsById = createMemo(() => Object.fromEntries(projects()?.map((project) => [project.id, project]) ?? []));

  const onCreateProject = async () => {
    const title = await callDialog(PromptDialog, {
      header: t('projects.new_dialog.header'),
      label: t('projects.new_dialog.label'),
      ok: t('projects.new_dialog.ok'),
    });
    if(title == null) return;
    await showErrorScope(async () => {
      const projectId = await api.postProjects({
        requestBody: {
          title,
        },
      });
      refetchProjects();
      navigate(linkProject(projectId));
    });
  };

  return <>
    <AppPage
      title={t('projects')}
      breadcrumb={() => <Link href={linkProjects}>{t('projects')}</Link>}
      uiClass="projects"
    />
    <Routes routes={[
      {
        path: '',
        component: () => <>
          <h1>
            <span>{t('projects')}</span>
            <div class="buttons">
              <Show when={(session()?.userPrivileges ?? []).indexOf('create_project') >= 0}>
                <Button onClick={onCreateProject}><FaIcon solid plus inline weak />{t('projects.new')}</Button>
              </Show>
            </div>
          </h1>
          <div class="list projects">
            <For each={projects()} fallback={
              <div>
                <ResourceFallback resource={projects} empty={<div class="empty">{t('projects.no_projects')}</div>} />
              </div>
            }>{(project) =>
              <Link href={project.userIsOwner || project.userIsListed ? linkProject(project.id) : linkStoreProject(project.id)}>
                <div class="title">
                  {project.title}
                  <Show when={project.isPublic}>
                    <FaIcon solid weak inline store />
                  </Show>
                </div>
                <div class="role">{t(`projects.management.users.role.${project.userIsOwner ? 'owner' : (project.userRole ?? 'starred')}`)}</div>
              </Link>
            }</For>
          </div>
          <div class="footer">
          </div>
        </>,
      },
      {
        component: (props) => {
          const project = createMemo(() => projectsById()[props.route]);
          return <Show when={project()} keyed fallback={<ResourceFallback resource={projects} page />}>{(project) => {

            const [projectQuotas] = createResource(async () => {
              return await api.getProjectsQuotas({
                project: project.id,
              });
            });

            const [branches, { refetch: refetchBranches }] = createResource(async () => {
              return await api.getProjectsBranches({
                project: project.id,
              });
            });

            const branchesById = createMemo(() => Object.fromEntries(branches()?.map((branch) =>
              [branch.id, branch]
            ) ?? []));

            const [versions, { refetch: refetchVersions }] = createResource(async () => {
              return await api.getProjectsVersions({
                project: project.id,
              });
            });

            const versionsById = createMemo(() => Object.fromEntries(versions()?.map((version) =>
              [version.id, version]
            ) ?? []));

            const [projectDescription] = createResource(async () => {
              return await api.getProjectsDescription({
                project: project.id,
              });
            });

            const onCreateBranch = async () => {
              const title = await callDialog(PromptDialog, {
                header: t('branches.new_dialog.header'),
                label: t('branches.new_dialog.label'),
                ok: t('branches.new_dialog.ok'),
              });
              if(title == null) return null;
              await showErrorScope(async () => {
                const branchId = await api.postProjectsBranches({
                  project: project.id,
                  requestBody: {
                    title,
                  },
                });
                navigate(linkProjectBranch(project.id, branchId));
              });
              refetchBranches();
            };

            const onProjectAction = async (key: string) => {
              switch(key) {
              case 'rename': {
                const title = await callDialog(PromptDialog, {
                  header: t('projects.rename_dialog.header'),
                  label: t('projects.rename_dialog.label'),
                  value: project.title,
                  ok: t('projects.rename_dialog.ok'),
                  focus: 'cancel',
                });
                if(title !== undefined) {
                  await showErrorScope(async () => {
                    await api.patchProjects({
                      project: project.id,
                      requestBody: {
                        title,
                      },
                    });
                  });
                  refetchProjects();
                }
                break;
              }
              case 'edit_description': {
                const description = await callDialog(PromptMarkdownDialog, {
                  header: t('projects.edit_description_dialog.header'),
                  label: t('projects.edit_description_dialog.label'),
                  value: projectDescription(),
                  projectId: project.id,
                  ok: t('projects.edit_description_dialog.ok'),
                  focus: 'cancel',
                });
                if(description !== undefined) {
                  await showErrorScope(async () => {
                    await api.putProjectsDescription({
                      project: project.id,
                      requestBody: description,
                    });
                  });
                  refetchProjects();
                }
                break;
              }
              case 'leave': {
                if(!await callDialog(ConfirmDialog, {
                  header: t('projects.leave_dialog.header'),
                  message: t('projects.leave_dialog.message')(<strong>{project.title}</strong>),
                  ok: t('projects.leave_dialog.ok'),
                  focus: 'cancel',
                  danger: true,
                })) return;
                await showErrorScope(async () => {
                  await api.postProjectsUsersLeave({
                    project: project.id,
                  });
                });
                refetchProjects();
                navigate(linkProjects);
                break;
              }
              case 'delete': {
                if(!await callDialog(ConfirmDialog, {
                  header: t('projects.delete_dialog.header'),
                  message: t('projects.delete_dialog.message')(<strong>{project.title}</strong>),
                  ok: t('projects.delete_dialog.ok'),
                  focus: 'cancel',
                  danger: true,
                })) return;
                await showErrorScope(async () => {
                  await api.deleteProjects({
                    project: project.id,
                  });
                });
                refetchProjects();
                navigate(linkProjects);
                break;
              }
              }
            };

            const Package = ({ branch, version, pkg}: {
              branch?: BranchInfo;
              version: VersionInfo;
              pkg: PackageInfo;
            }) => {
              type TagOsOption = keyof (typeof allTags)['os']["options"];
              type TagInfo = {
                os?: TagOsOption;
              };
              const tagsInfo = createMemo<TagInfo>(() => {
                const osTags: TagOsOption[] = [];
                const info: TagInfo = {};
                for(const tag of pkg.tags) {
                  const [tagType, tagValue] = tag.split(':');
                  if(!tagValue) continue;
                  switch(tagType) {
                  case 'os':
                    osTags.push(tagValue as TagOsOption);
                    break;
                  }
                }
                if(osTags.length == 1) {
                  info.os = osTags[0];
                }
                return info;
              });

              return (
                <Link href={linkProjectBranchVersionPackage({
                  projectId: project.id,
                  branchId: branch?.id,
                  versionId: version.id,
                  packageId: pkg.id,
                })}>
                  <Show when={tagsInfo().os}>{(os) => <FaIcon fa-2xl {...allTags.os.options[os()].icon} />}</Show>
                  <div class="title">{pkg.title}</div>
                  <PrettyDataSize size={pkg.size} />
                  <div class="published"><FormatDate date={new Date(pkg.created)} /></div>
                  <Tags tags={ISet(pkg.tags)} showIcons={false} />
                  <div class="buttons group">
                    <PackageActionButton project={project} branch={branch} version={version} pkg={pkg} />
                  </div>
                </Link>
              );
            };

            const VersionsSubroute = ({ branch } : {
              branch?: BranchInfo;
            }) => <>
              <Routes routes={[
                {
                  component: (props) => {
                    const version = createMemo(() => versionsById()[props.route]!);
                    return <Show when={version()} keyed fallback={<ResourceFallback resource={versions} page />}>{(version) => {

                      const [versionPackages] = createResource(version, async (version) => {
                        return await api.getProjectsVersionsPackages({
                          project: project.id,
                          version: version.id,
                        });
                      });

                      return <>
                        <AppPage
                          title={t('versions.header')(project.title, version.title)}
                          breadcrumb={() =>
                            <Link href={linkProjectBranchVersion({
                              projectId: project.id,
                              branchId: branch?.id,
                              versionId: version.id
                            })}>{version.title}</Link>
                          }
                          uiClass="version"
                          sidelinks={() => ({
                            header: t('projects.versions'),
                            items: (versions() ?? []).map((otherVersion) => ({
                              title: otherVersion.title,
                              link: linkProjectBranchVersion({
                                projectId: project.id,
                                branchId: branch?.id,
                                versionId: otherVersion.id,
                              }),
                              selected: otherVersion.id === version.id
                            })),
                          })}
                        />
                        <Routes routes={[
                          {
                            path: '',
                            component: () => {
                              const [versionBranches, { refetch: refetchVersionBranches }] = createResource(version, async (version) => {
                                return await api.getProjectsVersionsBranches({
                                  project: project.id,
                                  version: version.id,
                                });
                              });

                              const onPublish = async () => {
                                const branchId = await callDialog(PublishVersionDialog, {
                                  project,
                                  version,
                                });
                                if(!branchId) return;
                                await showErrorScope(async () => {
                                  await api.postProjectsBranchesVersions({
                                    project: project.id,
                                    branch: branchId,
                                    version: version.id,
                                  });
                                  refetchBranches();
                                  refetchVersionBranches();
                                });
                              };

                              const onDeployToSite = async () => {
                                if(!await callDialog(DeployToSiteDialog, {
                                  project,
                                  version,
                                })) return;
                              };

                              const onVersionAction = async (key: string) => {
                                switch(key) {
                                case 'rename': {
                                  const newVersionTitle = await callDialog(PromptDialog, {
                                    header: t('versions.rename_dialog.header'),
                                    label: t('versions.rename_dialog.label'),
                                    value: version.title,
                                    ok: t('versions.rename_dialog.ok'),
                                  });
                                  if(!newVersionTitle) return;
                                  await showErrorScope(async () => {
                                    await api.patchProjectsVersions({
                                      project: project.id,
                                      version: version.id,
                                      requestBody: {
                                        title: newVersionTitle,
                                      },
                                    });
                                  });
                                  refetchVersions();
                                  refetchBranches();
                                  break;
                                }
                                case 'edit_description': {
                                  const description = await callDialog(PromptMarkdownDialog, {
                                    header: t('versions.edit_description_dialog.header'),
                                    label: t('versions.edit_description_dialog.label'),
                                    value: {
                                      markdown: version.description,
                                      images: [],
                                    },
                                    ok: t('versions.edit_description_dialog.ok'),
                                    focus: 'cancel',
                                  });
                                  if(description !== undefined) {
                                    await showErrorScope(async () => {
                                      await api.patchProjectsVersions({
                                        project: project.id,
                                        version: version.id,
                                        requestBody: {
                                          description: description.markdown,
                                        },
                                      });
                                    });
                                    refetchVersions();
                                    refetchBranches();
                                  }
                                  break;
                                }
                                case 'delete': {
                                  if(!await callDialog(ConfirmDialog, {
                                    header: t('versions.delete_dialog.header'),
                                    message: t('versions.delete_dialog.message')(<strong>{version.title}</strong>),
                                    ok: t('versions.delete_dialog.ok'),
                                    focus: 'cancel',
                                    danger: true,
                                  })) return;
                                  await showErrorScope(async () => {
                                    await api.deleteProjectsVersions({
                                      project: project.id,
                                      version: version.id,
                                    });
                                  });
                                  refetchVersions();
                                  refetchBranches();
                                  navigate(branch ? linkProjectBranch(project.id, branch.id) : linkProject(project.id));
                                  break;
                                }
                                }
                              };

                              return <>
                                <h1>
                                  <span class="user-selectable">{t('versions.header')(project.title, version.title)}</span>
                                  <Show when={project.userPossibleActions.indexOf('patchVersion') >= 0}>
                                    <div class="buttons">
                                      <Button light onClick={() => onVersionAction('rename')}>{t('versions.rename')}</Button>
                                    </div>
                                  </Show>
                                  <div class="hint">{t('versions.header.hint')}</div>
                                </h1>
                                <h2>
                                  <span>{t('versions.description')}</span>
                                  <Show when={project.userPossibleActions.indexOf('patchVersion') >= 0}>
                                    <div class="buttons">
                                      <Button light onClick={() => onVersionAction('edit_description')}>{t('versions.edit_description')}</Button>
                                    </div>
                                  </Show>
                                </h2>
                                <MarkdownView markdown={() => ({
                                  markdown: version.description,
                                  lang: projectDescription()?.lang,
                                  images: [],
                                })} level={3} ugc linkInNewTab />
                                <h2>{t('versions.packages')}</h2>
                                <div class="list packages">
                                  <For each={versionPackages()} fallback={
                                    <div>
                                      <ResourceFallback resource={versionPackages} />
                                    </div>
                                  }>{(pkg) => Package({
                                    branch,
                                    version,
                                    pkg,
                                  })}</For>
                                </div>
                                <h2>
                                  <span>{t('versions.branches')}</span>
                                  <Show when={project.userPossibleActions.indexOf('publishVersion') >= 0}>
                                    <div class="buttons">
                                      <Button onClick={onPublish}><FaIcon solid plus inline weak />{t('versions.branches.publish')}</Button>
                                    </div>
                                  </Show>
                                </h2>
                                <div class="list branches">
                                  <For each={versionBranches()} fallback={
                                    <div>
                                      <ResourceFallback resource={versionBranches} empty={<div class="empty">{t('versions.branches.empty')}</div>} />
                                    </div>
                                  }>{(versionBranch) =>
                                    <Link href={linkProjectBranch(project.id, versionBranch.branchId)}>
                                      <div class="title">{versionBranch.branchTitle}</div>
                                      <div class="hint">{t(versionBranch.isCurrent ? 'versions.current' : 'versions.previous')}</div>
                                      <div class="published"><FormatDate date={new Date(versionBranch.published)} /></div>
                                    </Link>
                                  }</For>
                                </div>
                                <Show when={project.userPossibleActions.indexOf('deployVersionToSite') >= 0}>
                                  <h2>{t('versions.deploy_to_site.header')}</h2>
                                  <div class="buttons">
                                    <Button onClick={onDeployToSite}>{t('versions.deploy_to_site')}</Button>
                                  </div>
                                </Show>
                                <div class="footer">
                                  <ProjectActionsButtons project={project} title={t('versions.manage')} items={[
                                    {
                                      key: 'rename',
                                      title: t('versions.rename'),
                                      icon: <FaIcon solid pen inline weak />,
                                      action: 'patchVersion',
                                    },
                                    {
                                      key: 'edit_description',
                                      title: t('versions.edit_description'),
                                      icon: <FaIcon regular file-lines inline weak />,
                                      action: 'patchVersion',
                                    },
                                    {
                                      key: 'delete',
                                      title: t('versions.delete'),
                                      icon: <FaIcon regular trash-can inline weak />,
                                      danger: true,
                                      action: 'deleteVersion',
                                    },
                                  ]} onSelect={onVersionAction} />
                                </div>
                              </>;
                            },
                          },
                          {
                            path: 'packages',
                            component: () => PackagesSubroute({
                              branch,
                              version,
                              versionPackages,
                            }),
                          },
                        ]} />
                      </>;
                    }}</Show>;
                  },
                },
              ]} />
            </>;

            const PackagesSubroute = ({ branch, version, versionPackages, refetchVersionPackages }: {
              branch?: BranchInfo;
              version?: VersionInfo;
              versionPackages?: Resource<PackageInfo[]>;
              refetchVersionPackages?: () => void;
            }) => <>
              <Routes routes={[
                {
                  path: '',
                  component: () => {
                    return <>
                    </>;
                  },
                },
                {
                  component: (props) => {
                    const [pkg, { refetch: refetchPackage }] = createResource(() => props.route, async (route) => {
                      try {
                        return await api.getProjectsPackages1({
                          project: project.id,
                          _package: route,
                        });
                      } catch(e) {
                      }
                    });
                    return <Show when={pkg()} keyed fallback={<ResourceFallback resource={pkg} page />}>{(pkg) => <>
                      <AppPage
                        title={t('packages.header')(project.title, pkg.title)}
                        breadcrumb={() =>
                          <Link href={linkProjectBranchVersionPackage({
                            projectId: project.id,
                            branchId: branch?.id,
                            versionId: version?.id,
                            packageId: pkg.id,
                          })}>{pkg.title}</Link>
                        }
                        uiClass="package"
                        sidelinks={() => ({
                          header: t('projects.packages'),
                          items: (versionPackages?.() ?? []).map((otherPackage) => ({
                            title: otherPackage.title,
                            link: linkProjectBranchVersionPackage({
                              projectId: project.id,
                              branchId: branch?.id,
                              versionId: version?.id,
                              packageId: otherPackage.id,
                            }),
                            selected: otherPackage.id === pkg.id
                          })),
                        })}
                      />
                      <Routes routes={[
                        {
                          path: '',
                          component: () => {
                            const [rootEntry] = createResource(pkg, async (pkg) => {
                              const url = new URL(await api.getProjectsPackagesDownload({
                                project: project.id,
                                _package: pkg.id,
                              }), config.storageBaseUrl);
                              url.searchParams.set('filter', 'index');
                              return await readEntriesFromArchive(url.toString());
                            });

                            const onPackageAction = async (key: string) => {
                              switch(key) {
                              case 'rename': {
                                const newPackageTitle = await callDialog(PromptDialog, {
                                  header: t('packages.rename_dialog.header'),
                                  label: t('packages.rename_dialog.label'),
                                  value: pkg.title,
                                  ok: t('packages.rename_dialog.ok'),
                                });
                                if(!newPackageTitle) return;
                                await showErrorScope(async () => {
                                  await api.patchProjectsPackages({
                                    project: project.id,
                                    _package: pkg.id,
                                    requestBody: {
                                      title: newPackageTitle,
                                    },
                                  });
                                });
                                refetchPackage();
                                refetchVersionPackages?.();
                                break;
                              }
                              case 'edit_tags': {
                                const newPackageTags = await callDialog(EditPackageTagsDialog, {
                                  pkg,
                                });
                                if(newPackageTags == undefined) return;
                                await showErrorScope(async () => {
                                  await api.patchProjectsPackages({
                                    project: project.id,
                                    _package: pkg.id,
                                    requestBody: {
                                      tags: newPackageTags.toArray(),
                                    },
                                  });
                                });
                                refetchPackage();
                                refetchVersionPackages?.();
                                break;
                              }
                              case 'delete': {
                                if(!await callDialog(ConfirmDialog, {
                                  header: t('packages.delete_dialog.header'),
                                  message: t('packages.delete_dialog.message')(<strong>{pkg.title}</strong>),
                                  ok: t('packages.delete_dialog.ok'),
                                  focus: 'cancel',
                                  danger: true,
                                })) return;
                                await showErrorScope(async () => {
                                  await api.deleteProjectsPackages({
                                    project: project.id,
                                    _package: pkg.id,
                                  });
                                });
                                refetchVersionPackages?.();
                                navigate(version ? linkProjectBranchVersion({
                                  projectId: project.id,
                                  branchId: branch?.id,
                                  versionId: version.id,
                                }) : linkProject(project.id));
                                break;
                              }
                              }
                            };

                            return <>
                              <h1>
                                <span class="user-selectable">{t('packages.header')(project.title, pkg.title)}</span>
                                <Show when={project.userPossibleActions.indexOf('patchPackage') >= 0}>
                                  <div class="buttons">
                                    <Button light onClick={() => onPackageAction('rename')}>{t('packages.rename')}</Button>
                                  </div>
                                </Show>
                                <div class="hint">{t('packages.header.hint')}</div>
                              </h1>
                              <div class="buttons group">
                                <PackageActionButton project={project} branch={branch} version={version} pkg={pkg} />
                              </div>
                              <Tags tags={ISet(pkg.tags)} showCodes>
                                <Show when={project.userPossibleActions.indexOf('patchPackage') >= 0}>
                                  <Button light onClick={() => onPackageAction('edit_tags')}>{t('packages.edit_tags')}</Button>
                                </Show>
                              </Tags>
                              <Show when={rootEntry()} fallback={
                                <div class="message">
                                  <ResourceFallback resource={rootEntry} />
                                </div>
                              }>
                                <EntryContextProvider>
                                  <UploadBox root={rootEntry()!} entrypoint={createMemo(() => IList<string>(pkg.entrypoint))} />
                                </EntryContextProvider>
                                <div class="message">
                                  <Show when={pkg.entrypoint.length > 0} fallback={t('packages.entrypoint.empty')}>{
                                    t('packages.entrypoint')(<>
                                      <FaIcon solid play inline weak />
                                      <code>{pkg.entrypoint.join('/')}</code>
                                    </>)
                                  }</Show>
                                </div>
                                <div class="size">{
                                  t('packages.total_size')(
                                    <span class="inline_size"><PrettyDataSize size={rootEntry()!.totalSize()} /></span>
                                  )
                                }</div>
                              </Show>
                              <div class="footer">
                                <ProjectActionsButtons project={project} title={t('packages.manage')} items={[
                                  {
                                    key: 'rename',
                                    title: t('packages.rename'),
                                    icon: <FaIcon solid pen inline weak />,
                                    action: 'patchPackage',
                                  },
                                  {
                                    key: 'edit_tags',
                                    title: t('packages.edit_tags'),
                                    icon: <FaIcon solid tags inline weak />,
                                    action: 'patchPackage',
                                  },
                                  {
                                    key: 'delete',
                                    title: t('packages.delete'),
                                    icon: <FaIcon regular trash-can inline weak />,
                                    danger: true,
                                    action: 'deletePackage',
                                  },
                                ]} onSelect={onPackageAction} />
                              </div>
                            </>;
                          },
                        },
                        {
                          path: 'play',
                          component: () => {
                            const [spaceUrlResource] = createResource(async () => {
                              const spaceUrl = new URL(await api.getProjectsPackagesSpace({
                                project: project.id,
                                _package: pkg.id,
                              }));
                              spaceUrl.pathname += pkg.entrypoint.join('/');
                              return spaceUrl.toString();
                            });
                            return <WebPlayer spaceUrlResource={spaceUrlResource} />;
                          },
                        },
                      ]} />
                    </>}</Show>;
                  },
                },
              ]} />
            </>;

            return <>
              <AppPage
                title={project.title}
                breadcrumb={() => <Link href={linkProject(project.id)}>{project.title}</Link>}
                uiClass="project"
                sidelinks={() => ({
                  header: t('projects'),
                  items: (projects() ?? []).map((otherProject) => ({
                    title: otherProject.title,
                    link: linkProject(otherProject.id),
                    selected: otherProject.id === project.id
                  })),
                })}
              />
                <Routes routes={[
                  {
                    path: '',
                    component: () => <>
                      <h1>
                        <span class="user-selectable">{project.title}</span>
                        <Show when={project.userPossibleActions.indexOf('patchProject') >= 0}>
                          <div class="buttons">
                            <Button light onClick={() => onProjectAction('rename')}>{t('projects.rename')}</Button>
                          </div>
                        </Show>
                        <div class="hint">{t('projects.project.hint')}</div>
                      </h1>
                      <Show when={project.isPublic}>
                        <p>
                          <a href={linkStoreProject(project.id)} target="_blank">
                            <FaIcon solid weak inline store />
                            {t('projects.store_page')}
                          </a>
                        </p>
                      </Show>
                      <h2>
                        <span>{t('projects.description')}</span>
                        <Show when={project.userPossibleActions.indexOf('patchProject') >= 0}>
                          <div class="buttons">
                            <Button light onClick={() => onProjectAction('edit_description')}>{t('projects.edit_description')}</Button>
                          </div>
                        </Show>
                      </h2>
                      <Show when={projectDescription()} fallback={<ResourceFallback resource={projectDescription} />}>
                        <MarkdownView markdown={() => projectDescription()!} level={3} ugc linkInNewTab />
                      </Show>
                      <h2>
                        <span>{t('projects.branches')}</span>
                        <div class="buttons">
                          <Show when={project.userPossibleActions.indexOf('createBranch') >= 0}>
                            <Button onClick={onCreateBranch}><FaIcon solid plus inline weak />{t('branches.new')}</Button>
                          </Show>
                        </div>
                      </h2>
                      <div class="list branches">
                        <For each={branches()} fallback={
                          <div>
                            <ResourceFallback resource={branches} empty={<div class="empty">{t('branches.no_branches')}</div>} />
                          </div>
                        }>{(branch) =>
                          <Link href={linkProjectBranch(project.id, branch.id)}>
                            <div class="title">{branch.title}</div>
                            <Show when={branch.currentVersion}>
                              <div class="current_version">{branch.currentVersion!.version.title}</div>
                              <div class="current_version_published"><FormatDate date={new Date(branch.currentVersion!.published)} /></div>
                            </Show>
                          </Link>
                        }</For>
                      </div>
                      <div class="footer">
                        <div class="buttons align_end">
                          <Link href={linkProjectManage(project.id)}>{t('projects.management')}</Link>
                        </div>
                      </div>
                    </>,
                  },
                  {
                    path: 'branches',
                    component: () => <>
                      <Routes routes={[
                        {
                          component: (props) => {
                            const branch = createMemo(() => branchesById()[props.route]);
                            return <Show when={branch()} keyed fallback={<ResourceFallback resource={branches} page />}>{(branch) => {

                              const [branchVersions] = createResource(async () => {
                                return await api.getProjectsBranchesVersions({
                                  project: project.id,
                                  branch: branch.id,
                                });
                              });

                              const [currentVersionPackages] = createResource(branch.currentVersion, async (branchVersion) => {
                                if(!branchVersion) return;
                                return await api.getProjectsVersionsPackages({
                                  project: project.id,
                                  version: branchVersion.version.id,
                                });
                              });

                              const putVersion = async (versionId: string) => {
                                await showErrorScope(async () => {
                                  await api.postProjectsBranchesVersions({
                                    project: project.id,
                                    branch: branch.id,
                                    version: versionId,
                                  });
                                  refetchBranches();
                                });
                              };

                              const onPublishNewVersion = async () => {
                                const baseVersion = branch.currentVersion?.version;
                                const versionId = await callDialog(NewVersionDialog, {
                                  project: project,
                                  projectQuotas: projectQuotas(),
                                  branch,
                                  baseVersion,
                                  initialVersionTitle: calculateNextVersionTitle(baseVersion?.title) ?? '1.0.0',
                                });
                                if(!versionId) return;
                                refetchVersions();
                                await putVersion(versionId);
                              };

                              const onPublishExistingVersion = async () => {
                                const versionId = await callDialog(PublishExistingVersionDialog, {
                                  project: project,
                                  currentVersion: branch.currentVersion?.version,
                                });
                                if(!versionId) return;
                                await putVersion(versionId);
                              };

                              const onBranchAction = async (key: string) => {
                                switch(key) {
                                case 'rename': {
                                  const newBranchTitle = await callDialog(PromptDialog, {
                                    header: t('branches.rename_dialog.header'),
                                    label: t('branches.rename_dialog.label'),
                                    value: branch.title,
                                    ok: t('branches.rename_dialog.ok'),
                                  });
                                  if(!newBranchTitle) return;
                                  await showErrorScope(async () => {
                                    await api.patchProjectsBranches({
                                      project: project.id,
                                      branch: branch.id,
                                      requestBody: {
                                        title: newBranchTitle,
                                      },
                                    });
                                  });
                                  refetchBranches();
                                  break;
                                }
                                case 'delete': {
                                  if(!await callDialog(ConfirmDialog, {
                                    header: t('branches.delete_dialog.header'),
                                    message: t('branches.delete_dialog.message')(<strong>{branch.title}</strong>),
                                    ok: t('branches.delete_dialog.ok'),
                                    focus: 'cancel',
                                    danger: true,
                                  })) return;
                                  await showErrorScope(async () => {
                                    await api.deleteProjectsBranches({
                                      project: project.id,
                                      branch: branch.id,
                                    });
                                  });
                                  refetchBranches();
                                  navigate(linkProject(project.id));
                                  break;
                                }
                                }
                              };

                              const CurrentVersionButtons = () => <Show when={project.userPossibleActions.indexOf('publishVersion') >= 0}>
                                <div class="buttons group">
                                  <ActionDropdown actions={[
                                    {
                                      title: t('branches.publish_version.new'),
                                      icon: <FaIcon solid plus inline weak />,
                                      onAction: onPublishNewVersion,
                                    },
                                    {
                                      title: t('branches.publish_version.existing'),
                                      icon: <FaIcon solid retweet inline weak />,
                                      onAction: onPublishExistingVersion,
                                    },
                                  ]} />
                                </div>
                              </Show>;

                              return <>
                                <AppPage
                                  title={t('branches.header')(project.title, branch.title)}
                                  breadcrumb={() => <Link href={linkProjectBranch(project.id, branch.id)}>{branch.title}</Link>}
                                  uiClass="branch"
                                  sidelinks={() => ({
                                    header: t('projects.branches'),
                                    items: (branches() ?? []).map((otherBranch) => ({
                                      title: otherBranch.title,
                                      link: linkProjectBranch(project.id, otherBranch.id),
                                      selected: otherBranch.id === branch.id
                                    })),
                                  })}
                                />
                                <Routes routes={[
                                  {
                                    path: '',
                                    component: () => <>
                                      <h1>
                                        <span class="user-selectable">{t('branches.header')(project.title, branch.title)}</span>
                                        <Show when={project.userPossibleActions.indexOf('patchBranch') >= 0}>
                                          <div class="buttons">
                                            <Button light onClick={() => onBranchAction('rename')}>{t('branches.rename')}</Button>
                                          </div>
                                        </Show>
                                        <div class="hint">{t('branches.header.hint')}</div>
                                      </h1>
                                      <Show when={branch.currentVersion} fallback={
                                        <h2>
                                          <span>{t('branches.no_current_version')}</span>
                                          <CurrentVersionButtons />
                                        </h2>
                                      }>{(currentVersion) => <>
                                        <h2>
                                          <span>{t('branches.current_version')(
                                            <Link class="current_version" href={linkProjectBranchVersion({
                                              projectId: project.id,
                                              branchId: branch.id,
                                              versionId: currentVersion().version.id
                                            })}>{currentVersion().version.title}</Link>
                                          )}</span>
                                          <CurrentVersionButtons />
                                        </h2>
                                        <MarkdownView markdown={() => ({
                                          markdown: currentVersion().version.description,
                                          lang: projectDescription()?.lang,
                                          images: [],
                                        })} level={3} ugc linkInNewTab />
                                        <h3>{t('branches.current_version.packages')}</h3>
                                        <div class="list packages">
                                          <For each={currentVersionPackages()} fallback={
                                            <div>
                                              <ResourceFallback resource={currentVersionPackages} />
                                            </div>
                                          }>{(pkg) => Package({
                                            branch,
                                            version: currentVersion().version,
                                            pkg,
                                          })}</For>
                                        </div>
                                      </>}</Show>
                                      <h2>{t('branches.version_history')}</h2>
                                      <div class="list branch_versions">
                                        <For each={branchVersions()} fallback={
                                          <div>
                                            <ResourceFallback resource={branchVersions} empty={<div class="message">{t('branches.version_history.empty')}</div>} />
                                          </div>
                                        }>{(branchVersion) =>
                                          <>
                                            <Link href={linkProjectBranchVersion({
                                              projectId: project.id,
                                              branchId: branch.id,
                                              versionId: branchVersion.version.id
                                            })}>
                                              <div class="title">{branchVersion.version.title}</div>
                                              <MarkdownViewPlainTextLine
                                                markdown={() => branchVersion.version.description}
                                                lang={() => projectDescription()?.lang}
                                                classList={{
                                                  description: true,
                                                }}
                                              />
                                              <div class="hint">{t(branchVersion.isCurrent ? 'versions.current' : 'versions.previous')}</div>
                                              <div class="published"><FormatDate date={new Date(branchVersion.published)} /></div>
                                            </Link>
                                          </>
                                        }</For>
                                      </div>
                                      <Show when={(branchVersions()?.length ?? 0) > 0}>
                                        <div class="note"><FaIcon solid warning inline />{t('branches.version_history.note_removal')({
                                          quotaCount: projectQuotas()?.branchVersionsCount,
                                          quotaCountText: <strong>{projectQuotas()?.branchVersionsCount}</strong>,
                                          quotaNonCurrentTime: <strong>{Duration.fromObject({
                                            seconds: projectQuotas()?.branchVersionsNonCurrentTime,
                                          }, {
                                            locale: t('locale'),
                                          }).shiftTo('days').toHuman()}</strong>,
                                          quotaTime: projectQuotas()?.branchVersionsTime ? <strong>{Duration.fromObject({
                                            seconds: projectQuotas()?.branchVersionsTime,
                                          }, {
                                            locale: t('locale'),
                                          }).shiftTo('days').toHuman()}</strong> : null
                                        })}</div>
                                      </Show>
                                      <div class="footer">
                                        <ProjectActionsButtons project={project} title={t('branches.manage')} items={[
                                          {
                                            key: 'rename',
                                            title: t('branches.rename'),
                                            icon: <FaIcon solid pen inline weak />,
                                            action: 'patchBranch',
                                          },
                                          {
                                            key: 'delete',
                                            title: t('branches.delete'),
                                            icon: <FaIcon regular trash-can inline weak />,
                                            danger: true,
                                            action: 'deleteBranch',
                                          },
                                        ]} onSelect={onBranchAction} />
                                      </div>
                                    </>,
                                  },
                                  {
                                    path: 'versions',
                                    component: () => VersionsSubroute({ branch }),
                                  },
                                ]} />
                              </>;
                            }}</Show>;
                          },
                        },
                      ]} />
                    </>,
                  },
                  {
                    path: 'versions',
                    component: () => VersionsSubroute({}),
                  },
                  {
                    path: 'packages',
                    component: () => PackagesSubroute({}),
                  },
                  {
                    path: 'manage',
                    component: () => {
                      const [users, { refetch: refetchUsers }] = createResource(async () => {
                        const users = await api.getProjectsUsers({
                          project: project.id,
                        });
                        const collator = Intl.Collator(t('locale'));
                        return users.toSorted((a, b) => {
                          if(a.isOwner > b.isOwner) return -1;
                          if(a.isOwner < b.isOwner) return 1;
                          const roleOrder = -compareProjectUserRoles(a.role, b.role);
                          if(roleOrder != 0) return roleOrder;
                          return collator.compare(a.user.title, b.user.title);
                        });
                      });

                      const onSetProjectUser = (user: UserInfo, role?: ProjectUserRole) => showErrorScope(async () => {
                        if(role == undefined) {
                          if(!await callDialog(ConfirmDialog, {
                            header: t('projects.management.users.exile.dialog.header'),
                            message: t('projects.management.users.exile.dialog.message')(<strong>{project.title}</strong>, <strong>{user.title}</strong>),
                            ok: t('projects.management.users.exile.dialog.ok'),
                            danger: true,
                          })) return;
                        }
                        await api.putProjectsUsers({
                          project: project.id,
                          requestBody: {
                            user: user.id,
                            role: role,
                          },
                        });
                        refetchUsers();
                      });

                      const onOneTimeInvite = () => showErrorScope(async () => {
                        const {
                          token,
                        } = await api.postProjectsInvites({
                          project: project.id,
                          requestBody: {
                            approved: true,
                            expires: new Date(Date.now() + 24 * 3600 * 1000).toISOString(),
                            seats: 1,
                          },
                        });
                        const inviteUrl = new URL(linkInvite(token), window.location.origin).toString();
                        await callDialog(OkCopyDialog, {
                          header: t('projects.management.invites.one_time_invite.header'),
                          message: t('projects.management.invites.one_time_invite.message'),
                          text: inviteUrl,
                          note: t('projects.management.invites.one_time_invite.note'),
                        });
                      });

                      const [deploySites, { refetch: refetchDeploySites }] = createResource(async () => {
                        return await api.getProjectsDeploySites({
                          project: project.id,
                        });
                      });

                      const onAddDeploySite = async () => {
                        const deploySiteId = await callDialog(AddDeploySiteDialog, {
                          project,
                        });
                        if(deploySiteId) {
                          refetchDeploySites();
                        }
                      };

                      const onToggleProjectIsPublic = async () => {
                        const enable = !project.isPublic;

                        if(!await callDialog(ConfirmDialog, {
                          header: t(`projects.management.public_access.${enable ? 'enable' : 'disable'}_dialog.header`),
                          message: t(`projects.management.public_access.${enable ? 'enable' : 'disable'}_dialog.message`)(<strong>{project.title}</strong>),
                          focus: 'cancel',
                          ok: t(`projects.management.public_access.${enable ? 'enable' : 'disable'}_dialog.ok`),
                          danger: true,
                        })) return;

                        await showErrorScope(async () => {
                          await api.patchProjects({
                            project: project.id,
                            requestBody: {
                              isPublic: enable,
                            },
                          });
                        });

                        refetchProjects();
                      };

                      const onDeleteDeploySite = async (deploySite: DeploySiteInfo) => {
                        if(!await callDialog(ConfirmDialog, {
                          header: t('projects.management.deploy_sites.delete_dialog.header'),
                          message: t('projects.management.deploy_sites.delete_dialog.message')(<strong>{t(`deploy_credential_provider.${deploySite.provider}`)}</strong>, <strong>{deploySite.site}</strong>),
                          ok: t('projects.management.deploy_sites.delete_dialog.ok'),
                          danger: true,
                          focus: 'cancel',
                        })) return;

                        await showErrorScope(async () => {
                          await api.deleteProjectsDeploySites({
                            project: project.id,
                            deploySite: deploySite.id,
                          });
                        });

                        refetchDeploySites();
                      };

                      const onBranchTogglePublicAccess = async (branchId: string, e: Event & {
                        target: HTMLInputElement;
                      }) => {
                        e.target.disabled = true;
                        await showErrorScope(async () => {
                          await api.patchProjectsBranches({
                            project: project.id,
                            branch: branchId,
                            requestBody: {
                              isPublic: e.target.checked,
                            },
                          });
                        });
                        e.target.disabled = false;

                        refetchBranches();
                      }

                      return <>
                        <AppPage
                          title={t('projects.management.title')(project.title)}
                          breadcrumb={() => <Link href={linkProjectManage(project.id)}>{t('projects.management.breadcrumb')}</Link>}
                          uiClass="project_management"
                        />
                        <h1>
                          <span class="user-selectable">{project.title}</span>
                          <div class="hint">{t('projects.management.hint')}</div>
                        </h1>
                        <h2>
                          <span>{t('projects.management.users')}</span>
                          <Show when={project.userPossibleActions.indexOf('createInvite') >= 0}>
                            <div class="buttons">
                              <Button onClick={onOneTimeInvite}><FaIcon solid link inline weak />{t('projects.management.invites.one_time_invite')}</Button>
                            </div>
                          </Show>
                        </h2>
                        <div class="list project_users">
                          <For each={users()} fallback={
                            <div>
                              <ResourceFallback resource={users} empty={<div class="empty">{t('projects.management.users.empty')}</div>} />
                            </div>
                          }>{(user) =>
                            <div>
                              <FaIcon solid fa-2xl
                                user={!user.isOwner && user.role == 'tester'}
                                user-gear={!user.isOwner && user.role == 'developer'}
                                user-tie={!user.isOwner && user.role == 'admin'}
                                crown={user.isOwner}
                              />
                              <div class="title">{user.user.title}</div>
                              <Show when={!user.isOwner && project.userPossibleActions.indexOf('setUser') >= 0} fallback={
                                <div class="role">{t(`projects.management.users.role.${user.isOwner ? 'owner' : user.role}`)}</div>
                              }>
                                <Dropdown<ProjectUserRole | undefined, {
                                  key: ProjectUserRole | undefined;
                                  title: string;
                                  icon?: JSXElement;
                                  danger?: boolean;
                                }> classList={{
                                  role: true,
                                }} title={t(`projects.management.users.role.${user.role}`)} items={[
                                  ...projectUserRoles.toReversed().map((role) => ({
                                    key: role,
                                    title: t(`projects.management.users.role.${role}`),
                                    icon: <FaIcon regular circle={user.role != role} circle-dot={user.role == role} inline />,
                                  })),
                                  {
                                    key: undefined,
                                    title: t('projects.management.users.exile'),
                                    danger: true,
                                  },
                                ]} onSelect={(key) => onSetProjectUser(user.user, key)} />
                              </Show>
                            </div>
                          }</For>
                        </div>
                        <h2>
                          <span>{t('projects.management.deploy_sites')}</span>
                          <Show when={project.userPossibleActions.indexOf('createDeploySite') >= 0}>
                            <div class="buttons">
                              <Button onClick={onAddDeploySite}><FaIcon solid plus inline weak />{t('projects.management.deploy_sites.add')}</Button>
                            </div>
                          </Show>
                        </h2>
                        <div class="list deploy_sites">
                          <For each={deploySites()} fallback={
                            <div>
                              <ResourceFallback resource={deploySites} empty={<div class="empty">{t('projects.management.deploy_sites.empty')}</div>} />
                            </div>
                          }>{(deploySite) =>
                            <div>
                              <DeployProviderIcon provider={deploySite.provider} big />
                              <div class="site">{deploySite.site}</div>
                              <Show when={project.userPossibleActions.indexOf('deleteDeploySite') >= 0}>
                                <div class="buttons">
                                  <Button onClick={() => onDeleteDeploySite(deploySite)} danger>{t('projects.management.deploy_sites.delete')}</Button>
                                </div>
                              </Show>
                              <div class="provider">{t(`deploy_credential_provider.${deploySite.provider}`)}</div>
                            </div>
                          }</For>
                        </div>
                        <h2>{t('projects.management.public_access')}</h2>
                        <div class="list public_access">
                          <div>
                            <FaIcon solid fa-2xl {...{
                              lock: !project.isPublic,
                              bullhorn: project.isPublic,
                            }} />
                            <div class="message">{t(`projects.management.public_access.${project.isPublic ? 'enabled' : 'disabled'}`)}</div>
                            <div class="note">{t(`projects.management.public_access.${project.isPublic ? 'enabled' : 'disabled'}.note`)}</div>
                            <Show when={project.isPublic}>
                              <a class="store_link" href={linkStoreProject(project.id)} target="_blank">{t('projects.store_page')}</a>
                              <Show when={project.userPossibleActions.indexOf('patchBranch') >= 0}>
                                <h3>{t('projects.management.public_access.branches')}</h3>
                                <For each={branches()}>{(branch) =>
                                  <label class="branch"><input type="checkbox" checked={branch.isPublic} onChange={[onBranchTogglePublicAccess, branch.id]} />{branch.title}</label>
                                }</For>
                              </Show>
                            </Show>
                            <Show when={project.userPossibleActions.indexOf('patchProject') >= 0 && (project.isPublic || (session()?.userPrivileges ?? []).indexOf('store_publish') >= 0)}>
                              <div class="footer">
                                <div class="buttons">
                                  <Button danger onClick={onToggleProjectIsPublic}><FaIcon solid inline weak {...{
                                    lock: project.isPublic,
                                    bullhorn: !project.isPublic,
                                  }} />{t(`projects.management.public_access.${project.isPublic ? 'disable' : 'enable'}`)}</Button>
                                </div>
                              </div>
                            </Show>
                          </div>
                        </div>
                        <div class="footer">
                          <ProjectActionsButtons project={project} title={t('projects.management.manage')} items={[
                            {
                              key: 'rename',
                              title: t('projects.rename'),
                              icon: <FaIcon solid pen inline weak />,
                              action: 'patchProject',
                            },
                            {
                              key: 'edit_description',
                              title: t('projects.edit_description'),
                              icon: <FaIcon regular file-lines inline weak />,
                              action: 'patchProject',
                            },
                            {
                              key: 'leave',
                              title: t('projects.leave'),
                              danger: true,
                              action: 'leaveProject',
                            },
                            {
                              key: 'delete',
                              title: t('projects.delete'),
                              icon: <FaIcon regular trash-can inline weak />,
                              danger: true,
                              action: 'deleteProject',
                            },
                          ]} onSelect={onProjectAction} />
                        </div>
                      </>;
                    },
                  },
                  {
                    component: PageNotFound,
                  },
                ]} />
            </>;
          }}</Show>;
        },
      },
    ]} />
  </>;
};

// reasonably calculate next version, i.e. 1.0.0 => 1.0.1
const calculateNextVersionTitle = (versionTitle?: string) => {
  if(!versionTitle) return null;
  const pieces = versionTitle.split('.');
  if(pieces.length <= 0) return null;
  const lastPiece = pieces[pieces.length - 1]!;
  if(/^[0-9]+$/.exec(lastPiece)) {
    return pieces.toSpliced(pieces.length - 1, 1, String(Number.parseInt(lastPiece) + 1)).join('.');
  }
  return `${versionTitle}.1`;
};
